<template>
  <button
    type="button"
    class="float-trigger"
    :class="{ 'is-dragging': dragging }"
    :style="triggerStyle"
    aria-label="ICEChat"
    title="ICEChat"
    draggable="false"
    @click="handleClick"
    @mousedown="onDragStart"
  >
    <img src="@/images/aiChat/finscope-icon.gif" alt="" draggable="false" />
    <div v-if="unreadCount > 0" class="badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</div>
  </button>
</template>

<script>
// 悬浮球尺寸、拖拽时距离视口边缘的最小留白
const SIZE = 56;
const EDGE = 8;
// 默认距底部的距离，与原有样式保持一致
const DEFAULT_GAP = 24;
// 按下后位移超过该距离判定为拖动，避免拖动结束误触发点击打开面板
const DRAG_THRESHOLD = 4;
// 悬浮球位置缓存 key
const STORAGE_KEY = 'aiChatTriggerPos';

// 默认距底部的位置（横向固定贴右边）
function getDefaultY() {
  return window.innerHeight - SIZE - DEFAULT_GAP;
}

// 读取缓存的纵向位置，解析失败时返回 null 走默认值
function getStoredY() {
  let stored = null;
  try {
    stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch (error) {
    stored = null;
  }
  if (stored && typeof stored.y === 'number') {
    return stored.y;
  }
  return null;
}

// 限制坐标，保证悬浮球始终完整显示在可视区内
function clampPos(pos) {
  const maxX = Math.max(window.innerWidth - SIZE - EDGE, EDGE);
  const maxY = Math.max(window.innerHeight - SIZE - EDGE, EDGE);
  return {
    x: Math.min(Math.max(pos.x, EDGE), maxX),
    y: Math.min(Math.max(pos.y, EDGE), maxY),
  };
}

// 吸附到右边缘，只保留纵向位置
function snapRight(y) {
  return clampPos({ x: window.innerWidth - SIZE - EDGE, y });
}

export default {
  name: 'FloatTrigger',
  props: {
    unreadCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    const storedY = getStoredY();
    return {
      // 悬浮球左上角坐标，拖拽时实时更新
      pos: snapRight(storedY === null ? getDefaultY() : storedY),
      dragging: false,
      // 按下时鼠标与悬浮球左上角的偏移，用于计算跟随位置
      offsetX: 0,
      offsetY: 0,
      // 按下时的鼠标位置，用于判断是否超过拖动阈值
      startX: 0,
      startY: 0,
      // 本次按下是否发生过拖动，点击时据此区分
      moved: false,
    };
  },
  computed: {
    triggerStyle() {
      return { left: `${this.pos.x}px`, top: `${this.pos.y}px` };
    },
  },
  mounted() {
    window.addEventListener('resize', this.clampIntoView);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.clampIntoView);
    this.removeDragListeners();
  },
  methods: {
    handleClick() {
      // 拖动结束后浏览器仍会派发 click，这里直接忽略
      if (this.moved) {
        this.moved = false;
        return;
      }
      this.$emit('click');
    },
    savePos() {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.pos));
    },
    // 窗口尺寸变化（含浏览器缩放）时重新吸附到右边缘，纵向位置保持不变，不写缓存
    clampIntoView() {
      this.pos = snapRight(this.pos.y);
    },
    onDragStart(event) {
      // 只响应鼠标左键
      if (event.button !== 0) {
        return;
      }
      this.moved = false;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.offsetX = event.clientX - this.pos.x;
      this.offsetY = event.clientY - this.pos.y;
      document.addEventListener('mousemove', this.onDragMove);
      document.addEventListener('mouseup', this.onDragEnd);
    },
    onDragMove(event) {
      if (!this.dragging) {
        // 位移很小视为点击，不进入拖拽状态
        const isClick = Math.abs(event.clientX - this.startX) < DRAG_THRESHOLD
          && Math.abs(event.clientY - this.startY) < DRAG_THRESHOLD;
        if (isClick) {
          return;
        }
        this.dragging = true;
        this.moved = true;
      }
      this.pos = clampPos({
        x: event.clientX - this.offsetX,
        y: event.clientY - this.offsetY,
      });
    },
    onDragEnd() {
      this.removeDragListeners();
      if (!this.dragging) {
        return;
      }
      this.dragging = false;
      // 松手后统一吸附到右边缘，纵向保持松手位置
      this.pos = snapRight(this.pos.y);
      this.savePos();
    },
    removeDragListeners() {
      document.removeEventListener('mousemove', this.onDragMove);
      document.removeEventListener('mouseup', this.onDragEnd);
    },
  },
};
</script>

<style lang="scss" scoped>
.float-trigger {
  // 位置由组件内的 pos 通过 left/top 控制，支持拖动
  position: fixed;
  width: 56px;
  height: 56px;
  padding: 0;
  border: 1px solid #eef0f3;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1999;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ai-trigger-pulse 2s ease-in-out infinite;
  user-select: none;
  color: #333;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
  }

  &:active {
    transform: scale(0.95);
  }

  // 拖拽中关闭过渡，避免悬浮球跟手延迟；松手后恢复过渡用于吸附动画
  &.is-dragging {
    transition: none;
  }

  img {
    display: block;
    width: 48px;
    height: 48px;
    object-fit: contain;
    animation: ai-trigger-float 3s ease-in-out infinite;
    will-change: transform;
  }

  .badge {
    position: absolute;
    top: -2px;
    right: -2px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: #f56c6c;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    line-height: 18px;
    text-align: center;
    box-shadow: 0 2px 6px rgba(245, 108, 108, 0.4);
  }
}

@media (prefers-reduced-motion: reduce) {
  .float-trigger,
  .float-trigger img { animation: none; }
}

@keyframes ai-trigger-pulse {
  0%, 100% { box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12); }
  50% { box-shadow: 0 6px 22px rgba(50, 108, 209, 0.28); }
}

@keyframes ai-trigger-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}
</style>
